import { useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import SessionService from "../../../services/session_service";
import ActivityService from "../../../services/activity_service";
import MemberService from "../../../services/member_service";
import EmployeeService from "../../../services/employee_service";
import { useTenant } from "../../../hooks/useTenant";
import { toISODate } from "../../../utils/date";

/**
 * useEvaluationData
 * Hook para buscar dados da página de Avaliação.
 * Segue o mesmo padrão do useGradeData: busca sessões por data + dados estáticos.
 */
export const useEvaluationData = ({ referenceDate }) => {
    const { idTenant, loading: tenantLoading } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [activities, setActivities] = useState([]);
    const [members, setMembers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Busca sessões para o dia selecionado (como useGradeData faz com view="day")
    const fetchSessions = useCallback(async () => {
        if (!idTenant || tenantLoading) return;

        setLoading(true);
        try {
            const isoDate = toISODate(referenceDate);
            const result = await SessionService.getSessions(idTenant, isoDate, isoDate);

            const enriched = result.map(s => ({
                ...s,
                idSession: s.id,
            }));

            setSessions(enriched);
        } catch (error) {
            console.error("[useEvaluationData] Erro ao buscar sessões:", error);
            toast.error("Erro ao carregar sessões.");
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, [idTenant, tenantLoading, referenceDate]);

    // Busca dados estáticos (atividades e membros)
    const fetchStaticData = useCallback(async () => {
        if (!idTenant || tenantLoading) return;
        try {
            const [acts, mems, emps] = await Promise.all([
                ActivityService.getActivities(idTenant),
                MemberService.getMembers(idTenant),
                EmployeeService.getEmployees(idTenant),
            ]);
            setActivities(acts || []);
            setMembers(mems || []);
            setEmployees(emps || []);
        } catch (error) {
            console.error("[useEvaluationData] Erro ao buscar dados estáticos:", error);
        }
    }, [idTenant, tenantLoading]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            fetchSessions();
        }
    }, [tenantLoading, idTenant, fetchSessions]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            fetchStaticData();
        }
    }, [tenantLoading, idTenant, fetchStaticData]);

    const sortedEmployees = useMemo(() => {
        return [...(employees || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [employees]);

    return {
        sessions,
        activities,
        members,
        employees: sortedEmployees,
        loading,
        refresh: fetchSessions,
    };
};
